/**
 * Persistent in-app help drawer (Phase 3, N2).
 *
 * Pinned to the right edge on every authed page. Collapsed = a slim
 * "?" tab; expanded = a ~360px panel with three quick affordances:
 *
 *   1. "Try this in your AI chat" — a page-relevant prompt from the
 *      curated mcpPrompts library, with a Copy button.
 *   2. "Show me how on this page" — fires the page's coachmark playlist
 *      via the global window.__restartTour delegate (Phase 2).
 *   3. Quick links to the MCP marketing page + the full Help center.
 *
 * Persists open/closed in localStorage. Never auto-opens (B1 F-UX-2) —
 * the collapsed tab pulses until the user opens the drawer for the first
 * time, and after that it only opens when explicitly clicked.
 *
 * Mounted once from main.js after auth boots. Safe to call init multiple
 * times (idempotent).
 */

import { pickPromptForRoute, ROUTE_TO_CATEGORY } from '../data/mcpPrompts.js';
import { close as iconClose, arrowRight, check as iconCheck } from './icons.js';

const STORAGE_OPEN = 'heist.helpDrawer.open';
const STORAGE_FIRST_OPEN_AT = 'heist.helpDrawer.firstOpenedAt';

let drawerEl = null;
let isOpen = false;
let lastRenderedRoute = null;

export function initHelpDrawer() {
    if (drawerEl) return; // idempotent

    drawerEl = document.createElement('aside');
    drawerEl.className = 'help-drawer';
    drawerEl.dataset.open = 'false';
    drawerEl.setAttribute('aria-label', 'Help');
    document.body.appendChild(drawerEl);

    drawerEl.addEventListener('click', onClick);

    // Re-render whenever the route changes so the suggested prompt + tour
    // hint match the page the user is currently on.
    window.addEventListener('hashchange', () => {
        // Cheap optimization — only re-render when the route actually
        // changed, not on every fragment-only update.
        const r = currentRoute();
        if (r !== lastRenderedRoute) render();
    });
    // Some pages set body.dataset.route after their own async mount, so
    // also poll the next animation frame after navigation.
    requestAnimationFrame(render);

    // Read persisted open state.
    try {
        if (localStorage.getItem(STORAGE_OPEN) === '1') {
            isOpen = true;
            drawerEl.dataset.open = 'true';
        }
    } catch (_) { /* private mode */ }

    // The drawer no longer auto-opens for new accounts (B1 F-UX-2 — it
    // covered the first generation result and stacked on the other
    // onboarding surfaces). The pulsing tab hint below keeps it
    // discoverable until the user opens it once.
    render();
}

function currentRoute() {
    // Prefer the router-set body.dataset.route (most accurate after the
    // first navigation has settled) but fall back to the hash directly
    // because initHelpDrawer() runs BEFORE the first router.go() — without
    // this fallback, the auto-open check would always think it's on
    // `home` regardless of where the deep-link landed the user.
    const fromBody = document.body.dataset.route;
    if (fromBody) return fromBody;
    const hash = (location.hash || '#home').replace('#', '').split('?')[0].trim();
    return hash || 'home';
}

function readFirstOpenAt() {
    try { return localStorage.getItem(STORAGE_FIRST_OPEN_AT); } catch (_) { return null; }
}

// Router uses `memory` for the route key but the coachmark playlist is
// keyed `brain` (matching the page-header tour button on that page). All
// other routes use the same name as their playlist. Centralise the
// mapping so adding a new page is a one-line change.
const ROUTE_TO_TOUR = {
    home: 'home',
    memory: 'brain',
    create: 'create',
    library: 'library',
    calendar: 'calendar',
    campaigns: 'campaigns',
    analytics: 'analytics',
    connectors: 'connectors',
};

function render() {
    if (!drawerEl) return;
    const route = currentRoute();
    lastRenderedRoute = route;
    const tourName = ROUTE_TO_TOUR[route] || null;
    const promptCard = pickPromptForRoute(route);
    const knownTour = !!tourName;
    // Discoverability hint: if the drawer has never been opened, mark
    // the tab with `data-hint="pulse"` so CSS gives it a soft pulsing
    // glow. Cleared as soon as the user opens it for the first time.
    const neverOpened = !readFirstOpenAt();

    drawerEl.innerHTML = `
        <button class="help-drawer__tab" type="button" data-action="toggle" data-hint="${neverOpened && !isOpen ? 'pulse' : ''}" aria-label="${isOpen ? 'Close tips' : 'Open tips for this page'}" title="${isOpen ? 'Close tips' : 'Tips for this page'}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"/><path d="M5 19l.5 1.5L7 21l-1.5.5L5 23l-.5-1.5L3 21l1.5-.5L5 19z"/></svg>
            <span class="help-drawer__tab-label">Tips</span>
        </button>
        <div class="help-drawer__panel" role="dialog" aria-label="Help and shortcuts">
            <div class="help-drawer__header">
                <div class="help-drawer__title">Help</div>
                <button class="help-drawer__close" type="button" data-action="toggle" aria-label="Close help">${iconClose()}</button>
            </div>

            <section class="help-drawer__section">
                <div class="help-drawer__eyebrow">Try this in your AI chat</div>
                <div class="help-drawer__prompt-card">
                    <div class="help-drawer__prompt-title">${escAttr(promptCard.title)}</div>
                    <code class="help-drawer__prompt-body">${escHtml(promptCard.prompt)}</code>
                    <div class="help-drawer__prompt-actions">
                        <span class="help-drawer__prompt-cat">${escHtml(promptCard.category)}</span>
                        <button class="help-drawer__btn help-drawer__btn--primary" type="button" data-action="copy-prompt" data-prompt="${escAttr(promptCard.prompt)}">Copy prompt</button>
                    </div>
                </div>
                <div class="help-drawer__hint">${promptCard.why ? escHtml(promptCard.why) : ''}</div>
            </section>

            ${knownTour ? `
            <section class="help-drawer__section">
                <div class="help-drawer__eyebrow">Show me how</div>
                <button class="help-drawer__btn help-drawer__btn--full" type="button" data-action="run-tour" data-playlist="${escAttr(tourName)}">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="10 8 16 12 10 16 10 8"/></svg>
                    Tour this page
                </button>
            </section>
            ` : ''}

            ${route === 'create' ? `
            <section class="help-drawer__section help-drawer__section--quiet" data-testid="help-drawer-create-video-link">
                <a class="help-drawer__link" href="#help?q=faceless+video"><span>🎬 Make video & images</span>${arrowRight()}</a>
            </section>
            ` : ''}

            <section class="help-drawer__section help-drawer__section--quiet">
                <a class="help-drawer__link" href="#help?tab=mcp"><span>What is MCP?</span>${arrowRight()}</a>
                <a class="help-drawer__link" href="#help"><span>Open the full Help center</span>${arrowRight()}</a>
            </section>
        </div>
    `;
}

function onClick(e) {
    const target = e.target.closest('[data-action]');
    if (!target) return;
    const action = target.dataset.action;

    if (action === 'toggle') {
        isOpen = !isOpen;
        drawerEl.dataset.open = isOpen ? 'true' : 'false';
        try {
            if (isOpen) localStorage.setItem(STORAGE_OPEN, '1');
            else localStorage.removeItem(STORAGE_OPEN);
        } catch (_) {}
        // Mark first-opened-at so future sessions don't auto-pop the drawer
        // on top of someone who already met it.
        try {
            if (isOpen && !localStorage.getItem(STORAGE_FIRST_OPEN_AT)) {
                localStorage.setItem(STORAGE_FIRST_OPEN_AT, new Date().toISOString());
            }
        } catch (_) {}
        render();
        return;
    }

    if (action === 'copy-prompt') {
        copyText(target.dataset.prompt || '', target);
        return;
    }

    if (action === 'run-tour') {
        const name = target.dataset.playlist;
        if (typeof window.__restartTour === 'function') {
            // Close the drawer first so the spotlight ring isn't anchored
            // behind it. Persist the closed state for the rest of the
            // session — a returning visitor probably wants to see the tour
            // unobstructed.
            isOpen = false;
            drawerEl.dataset.open = 'false';
            try { localStorage.removeItem(STORAGE_OPEN); } catch (_) {}
            // Defer the tour fire until after the closing transition so the
            // spotlight measures rects against the post-collapse layout.
            setTimeout(() => { try { window.__restartTour(name); } catch (_) {} }, 220);
        }
        return;
    }
}

function copyText(text, btn) {
    // innerHTML preserves the SVG check icon in the success flash.
    const orig = btn.innerHTML;
    const flash = (markup) => {
        btn.innerHTML = markup;
        setTimeout(() => { btn.innerHTML = orig; }, 1200);
    };
    const okMarkup = `<span class="i-svg-inline">${iconCheck()}</span> Copied`;
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => flash(okMarkup), () => flash('Copy failed'));
            return;
        }
    } catch (_) { /* fall through */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flash(okMarkup);
    } catch (_) {
        flash('Copy failed');
    }
}

function escHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
function escAttr(s) {
    return escHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Public toggle — used by the page-header "?" button (Phase 3d). The
 * floating tab is hidden in CSS so this is the canonical way to open
 * the drawer from anywhere in the app.
 */
export function toggleHelpDrawer() {
    if (!drawerEl) return;
    isOpen = !isOpen;
    drawerEl.dataset.open = isOpen ? 'true' : 'false';
    try {
        if (isOpen) localStorage.setItem(STORAGE_OPEN, '1');
        else localStorage.removeItem(STORAGE_OPEN);
        if (isOpen && !localStorage.getItem(STORAGE_FIRST_OPEN_AT)) {
            localStorage.setItem(STORAGE_FIRST_OPEN_AT, new Date().toISOString());
        }
    } catch (_) {}
    render();
}

/**
 * Tear-down — used by tests. The drawer is normally singleton-mounted in
 * main.js for the lifetime of the session.
 */
export function destroyHelpDrawer() {
    if (drawerEl && drawerEl.parentNode) drawerEl.parentNode.removeChild(drawerEl);
    drawerEl = null;
    isOpen = false;
    lastRenderedRoute = null;
}
